import React, { useState, useMemo } from 'react';
import { connect } from 'react-redux';
import { Redirect } from 'react-router-dom';
import { withStyles, Tooltip, IconButton, Typography, Dialog, DialogTitle, DialogContent } from '@material-ui/core';
import { hasPermission, createHistory, formatDate, hasAtLeastOnePermission } from '../../sc-utilities';
import TableViewList from './TableViewList';
import { FindInPage, More } from '@material-ui/icons';
// import { CancelToken } from 'axios';

const styles = theme => ({
    root: {
        width: `98.9%`,
        height: 'auto',
        paddingLeft: theme.spacing(),
        [theme.breakpoints.down('sm')]: {
            padding: 0,
            margin: 0,
            width: '100%'
        }
    }
});

const TableView = props => {
    const tableRef = React.createRef();
    const { classes, columns, translations, permissions } = props;
    const [redirectOptions, setRedirectOptions] = useState({ redirect: false, TableViewID: null, redirectPath: '' });
    const [paramsDialog, setParamsDialog] = useState({ open: false });
    let visible = columns && columns['TableView'];

    const tableColumns = useMemo(() => {
        return [
            {
                title: translations["action"] || "Action", field: 'Action',
                hidden: visible ? visible['Action'] : false,
                cellStyle: { minWidth: 100, maxWidth: 100 },
                filtering: false,
                render: rowData => <>
                    {
                        hasPermission(permissions, 'table_view_load') ? <Tooltip title={translations["table_view_details"] || "Table view details"}>
                            < IconButton value={rowData.TableViewID} aria-label={translations["table_view_details"] || "Table view details"} onClick={() => redirectHandler(`/admin/tableView/edit?TableViewID=${rowData.TableViewID}`)}>
                                < FindInPage />
                            </IconButton>
                        </Tooltip > : <div></div>
                    }
                    {
                        hasPermission(permissions, 'table_view_load') ? <Tooltip title={translations["table_view_parameters"] || "Table view parameters"}>
                            < IconButton value={rowData.TableViewID} aria-label={translations["table_view_parameters"] || "Table view parameters"} onClick={() => {
                                try {
                                    let paramsInfo = JSON.parse(rowData?.Parameters);
                                    if (!paramsInfo) {
                                        throw new Error('No user agent information');
                                    }
                                    setParamsDialog({ ...paramsDialog, open: true, details: paramsInfo });
                                } catch (error) {
                                    console.log('No params found')
                                }
                            }}>
                                < More />
                            </IconButton>
                        </Tooltip > : <div></div>
                    }
                </>
            },
            {
                title: translations["name"] || 'Name', field: 'Name',
                hidden: visible ? visible['Name'] : false,
                filtering: true,
                render: rowData => {
                    return <Typography variant={'caption'}>{rowData.Name ? rowData.Name : 'n/a'}</Typography>;
                }
            },
            {
                title: translations["query_type"] || 'QueryType', field: 'QueryType',
                hidden: visible ? visible['QueryType'] : false,
                filtering: true,
                render: rowData => {
                    return <Typography variant={'caption'}>{rowData?.QueryType}</Typography>;
                }
            },
            {
                title: translations["created_at"] || 'Created at', field: 'CreatedAt',
                hidden: visible ? visible['CreatedAt'] : false,
                filtering: true,
                render: rowData => {
                    return <Typography variant={'caption'}>{rowData.CreatedAt ? formatDate(rowData.CreatedAt) : '-'}</Typography>;
                }
            },
            {
                title: translations["last_modified_at"] || 'Last Modified At', field: 'LastModifiedAt',
                hidden: visible ? visible['LastModifiedAt'] : false,
                filtering: true,
                width: 100,
                render: rowData => {
                    return <Typography variant={'caption'}>{rowData?.LastModifiedAt ? formatDate(rowData.LastModifiedAt) : '-'}</Typography>;
                }
            },
            {
                title: translations["deleted"] || 'Deleted', field: 'Deleted',
                hidden: visible ? visible['Deleted'] : false,
                filtering: true,
                width: 200,
                lookup: { '1': translations['yes'] || 'Yes', '0': translations['no'] || 'No' },
                defaultFilter: ['0', '1'],
                render: rowData => {
                    return <Typography variant={'caption'}>{rowData.Deleted ? translations['yes'] || 'Yes' : translations['no'] || 'No'}</Typography>;
                }
            },
        ];
    }, [translations, visible, permissions, paramsDialog])

    if (!hasAtLeastOnePermission(permissions, ['table_view_load'])) {
        tableColumns.shift();
    }

    const redirectHandler = (path, TableViewID = null) => {
        createHistory();
        setRedirectOptions({ redirect: true, TableViewID: TableViewID, redirectPath: path })
    };

    if (redirectOptions.redirect) {
        return <Redirect to={redirectOptions.redirectPath} />;
    }

    let details = [];
    if (paramsDialog?.details) {
        for (let [key, value] of Object.entries(paramsDialog?.details)) {
            details.push({ key, value })
        }
    }
    const getTranslate = (element) => {
        switch (element.key) {
            case 'Action':
                return 'action';
            case 'CustomerName':
                return 'customer_name';
            case 'WipeStarted':
                return 'wipe_started';
            case 'OrderInventoryID':
                return 'order';
            case 'TransportColliInventoryID':
                return 'transport_colli';
            case 'ColliInventoryID':
                return 'colli';
            case 'InventoryID':
                return 'inventory_id';
            case 'Vendor':
                return 'vendor';
            case 'Model':
                return 'model';
            case 'DiagnoseSessionCases':
                return 'diagnose_result';
            case 'WipeStatus':
                return 'wipe_status';
            case 'WipeMethod':
                return 'wipe_method';
            default:
                break;
        }
    }

    const formatFilterValue = (value) => {
        return Array.isArray(value['filterValue']) ? '[' + value['filterValue'].map(e => e) + ']' : value['filterValue'] ? value['filterValue'] : 'null'
    }
    return (
        <div className={classes.root}>
            {paramsDialog.open ?
                <Dialog
                    id="params-dialog"
                    open={paramsDialog.open}
                    disableBackdropClick={false}
                    disableEscapeKeyDown={false}
                    disableEnforceFocus={false}
                    fullWidth={true}
                    onClose={() => setParamsDialog({ open: false })}
                >
                    <DialogTitle id="params-dialog-title">{translations['params_dialog_info'] || 'Table View Parameters information'}</DialogTitle>
                    <DialogContent dividers>
                        {(details[0]?.value ?? []).map(el =>
                            <Typography variant={'caption'} key={el.title} gutterBottom><b>{translations[getTranslate(el.title)] || el.title}: </b> {translations['hidden'] || 'Hidden'}: {el['hidden'] === false ? translations['no'] || 'No' : translations['yes'] || 'Yes'} ,{translations['column_order'] || 'Column Order'}: {el['columnOrder']},{translations['filter_value'] || 'Filter value'}: {el?.tableData?.filterValue ? formatFilterValue(el?.tableData?.filterValue) : null}<br></br> </Typography>
                        )}
                    </DialogContent>
                </Dialog>
                : null}
            {hasPermission(permissions, 'table_view_list') ?
                <TableViewList
                    tableColumns={tableColumns}
                    redirectHandler={redirectHandler}
                    tableRef={tableRef}
                />
                :
                null}
        </div>
    );
};

const mapStateToProps = state => {
    return {
        translations: state.translate && state.translate.translations ? state.translate.translations.activeTranslations || {} : {},
        columns: state.core.login.userSettings ? state.core.login.userSettings.Columns : null,
        filters: state?.core?.login?.userSettings?.ColumnOrders,
        permissions: state?.core?.login?.permissions || [],
        activeCustomerID: state.core.login.activeCustomer.CustomerID,
    };
};

export default connect(
    mapStateToProps,
    null
)(withStyles(styles)(TableView));
